import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import AdminNavBar from "../components/AdminNavBar";
import {
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  AdminLoadingState,
} from "../components/AdminPageShell";
import { useAdminResourcePersons } from "../store/admin";
import ResourcePersonPagination from "./components/resourcePersons/ResourcePersonPagination";
import {
  formatDate,
  progressPercent,
  statusBadgeClass,
  studentDisplayName,
} from "./components/resourcePersons/resourcePersonUtils";

// ===== RESOURCE PERSON STUDENTS PAGE — GET /api/admin/resource-persons/:id/students =====
const ResourcePersonStudentsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    resourcePersonStudents,
    resourcePersonStudentsLoading,
    resourcePersonStudentsError,
    resourcePersonStudentsTotal,
    resourcePersonStudentsPages,
    fetchResourcePersonStudents,
  } = useAdminResourcePersons();

  const [page, setPage] = useState(1);

  // GET /api/admin/resource-persons/:id/students?page&limit
  useEffect(() => {
    if (id) {
      fetchResourcePersonStudents(id, { page, limit: 10 });
    }
  }, [id, page, fetchResourcePersonStudents]);

  const goBack = () => navigate("/admin/resource-persons");

  return (
    <div>
      <AdminNavBar
        title="Resource Person"
        subTitle="Students"
        context="See the students learning under this resource person."
      />
      <AdminPageBody>
        <div className="mb-3">
          <button
            onClick={goBack}
            className="mb-2 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Resource Persons
          </button>
          <h4 className="mt-2 text-lg font-bold text-slate-900">Students</h4>
        </div>

        <AdminSectionHeader
          title="Enrolled Students"
          description="Students enrolled in this resource person's courses."
          count={`${resourcePersonStudentsTotal} students`}
        />

        <AdminTablePanel className="mt-4">
          {resourcePersonStudentsLoading ? (
            <AdminLoadingState />
          ) : resourcePersonStudentsError ? (
            <div className="p-4 text-sm text-red-600">{resourcePersonStudentsError}</div>
          ) : resourcePersonStudents.length === 0 ? (
            <div className="d-flex justify-content-center align-items-center tableLoader">
              No students found for this resource person.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="admin-table-responsive w-full border-collapse text-[13px]"
                style={{ minWidth: "720px" }}
              >
                <thead>
                  <tr>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Student</th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Email</th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Course</th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Progress</th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Status</th>
                    <th className="border-b border-slate-200 px-3 py-2.5 text-left text-slate-600">Enrolled On</th>
                  </tr>
                </thead>
                <tbody>
                  {resourcePersonStudents.map((student, i) => {
                    const percent = progressPercent(student);
                    return (
                      <tr key={student.id ?? i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td data-label="Student" className="px-3 py-2.5 font-medium text-slate-800">
                          {studentDisplayName(student)}
                        </td>
                        <td data-label="Email" className="px-3 py-2.5 text-slate-600">
                          {student.email || "--"}
                        </td>
                        <td data-label="Course" className="px-3 py-2.5 text-slate-600">
                          {student.latestCourse || student.courseTitle || "—"}
                          {student.courseCount ? (
                            <span className="ml-1 text-xs text-slate-400">
                              ({student.courseCount} course{student.courseCount === 1 ? "" : "s"})
                            </span>
                          ) : null}
                        </td>
                        <td data-label="Progress" className="px-3 py-2.5">
                          {percent !== null ? (
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100">
                                <div
                                  className="h-full rounded-full bg-[#16213A]"
                                  style={{ width: `${Math.min(percent, 100)}%` }}
                                />
                              </div>
                              <span className="text-xs font-semibold text-slate-600">{percent}%</span>
                            </div>
                          ) : (
                            "--"
                          )}
                        </td>
                        <td data-label="Status" className="px-3 py-2.5">
                          <span className={statusBadgeClass(student.completed ? "ACTIVE" : "PENDING")}>
                            {student.completed ? "Completed" : "In progress"}
                          </span>
                        </td>
                        <td data-label="Enrolled On" className="px-3 py-2.5 text-slate-500">
                          {formatDate(student.latestEnrollmentDate)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </AdminTablePanel>

        {!resourcePersonStudentsLoading &&
          !resourcePersonStudentsError &&
          resourcePersonStudents.length !== 0 && (
            <div className="mt-4">
              <ResourcePersonPagination
                page={page}
                lastPage={resourcePersonStudentsPages}
                onPageChange={setPage}
              />
            </div>
          )}
      </AdminPageBody>
    </div>
  );
};

export default ResourcePersonStudentsPage;